import { type JSX, useEffect, useRef, useState } from "react"
import { fetchConversationMessages, type InboxMessageRecord, type InboxUser, sendInboxMessage } from "./api"

const POLL_INTERVAL_MS = 2000

// One open conversation: the transcript, polled on a short tick so the other
// person's replies appear as they happen, and the composer. Same cursor
// pattern as the workspace agent chat. Every message that lands here is also
// reported up through onLatest so the conversation list stays current.
export function ConversationPane({
  messagesUrl,
  conversationKey,
  otherUser,
  onLatest,
  onBack,
}: {
  messagesUrl: string
  conversationKey: string
  otherUser: InboxUser | null
  onLatest: (message: InboxMessageRecord) => void
  onBack: () => void
}): JSX.Element {
  const [messages, setMessages] = useState<InboxMessageRecord[]>([])
  const [draft, setDraft] = useState("")
  const [sending, setSending] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const lastIdRef = useRef(0)
  const bottomRef = useRef<HTMLDivElement>(null)
  const onLatestRef = useRef(onLatest)
  onLatestRef.current = onLatest

  // Poll for new rows. The cursor lives in a ref so the interval callback
  // always sees the latest without re-arming the timer.
  useEffect(() => {
    let cancelled = false

    async function pull(): Promise<void> {
      const batch = await fetchConversationMessages(messagesUrl, conversationKey, lastIdRef.current)
      if (cancelled || !batch || batch.messages.length === 0) return
      lastIdRef.current = batch.last_id
      setMessages((current) => [ ...current, ...batch.messages ])
      onLatestRef.current(batch.messages[batch.messages.length - 1])
    }

    void pull()
    const timer = setInterval(() => void pull(), POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [messagesUrl, conversationKey])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" })
  }, [messages.length])

  async function send(): Promise<void> {
    const contents = draft.trim()
    if (!contents || sending || !otherUser) return
    setSending(true)
    const result = await sendInboxMessage(messagesUrl, otherUser.id, contents)
    setSending(false)

    if (typeof result === "string") {
      setProblem(result)
      return
    }
    setProblem(null)
    setDraft("")
    if (result.id > lastIdRef.current) {
      lastIdRef.current = result.id
      setMessages((current) => [ ...current, result ])
      onLatestRef.current(result)
    }
  }

  return (
    <section className="inbox__pane" aria-label="Conversation">
      <header className="inbox__pane-head">
        <button type="button" className="inbox__back" aria-label="Back to conversations" onClick={onBack}>
          ←
        </button>
        <h1 className="inbox__pane-title">{otherUser?.name ?? "Conversation"}</h1>
      </header>

      <div className="inbox__messages">
        {messages.length === 0 && <p className="inbox__messages-empty">No messages yet. Say hello.</p>}
        {messages.map((message) => (
          <MessageTurn key={message.id} message={message} />
        ))}
        <div ref={bottomRef} />
      </div>

      {problem && <p className="inbox__problem" role="alert">{problem}</p>}

      <form
        className="inbox__composer"
        onSubmit={(event) => {
          event.preventDefault()
          void send()
        }}
      >
        <input
          type="text"
          className="inbox__input"
          placeholder="Send a message…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <button type="submit" className="btn btn--primary" disabled={sending || !draft.trim()}>
          {sending ? "Sending…" : "Send"}
        </button>
      </form>
    </section>
  )
}

function MessageTurn({ message }: { message: InboxMessageRecord }): JSX.Element {
  return (
    <div className={`inbox__turn inbox__turn--${message.sent_by_self ? "self" : "other"}`}>
      <div className="inbox__bubble">{message.contents}</div>
    </div>
  )
}
